import { styled } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Typography, { TypographyProps } from '@mui/material/Typography'
import { equals, reduce, sort } from 'ramda'
import React from 'react'

import TablePagination from '@/app/component/atom/table-pagination'
import { TableColumn } from '@/config/types'

import ColumnActions from './column-actions'

const CELL_WIDTH = 200

const PAGE_SIZE_VALUES = [
  {
    label: '5',
    value: 5,
  },
  {
    label: '10',
    value: 10,
  },
  {
    label: '30',
    value: 30,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
]

type ContactDataType = any

type Selection = {
  add: (values: number[]) => void
  isSelected: (value: number) => boolean
  remove: (values: number[]) => void
}

type Props = {
  columns: TableColumn[]
  data: ContactDataType[]
  height?: string | number
  noData: JSX.Element
  page?: number
  pageSize?: number
  selectAll?: JSX.Element
  selection?: Selection
  style?: React.CSSProperties
  tableActions: React.ReactNode
  total: number
  width?: string | number

  editAction?: (payload: { id: TableColumn['accessor']; title: TableColumn['header'] }) => void
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  rowAction: (rowId: string | number) => void
}

export default function PowerTable(props: Props) {
  const {
    columns,
    data,
    height,
    noData,
    page = 1,
    pageSize = 10,
    selectAll,
    selection,
    style = {},
    tableActions,
    total,
    width,
    editAction,
    onPageChange,
    onPageSizeChange,
    rowAction,
  } = props
  const [memoedColumns, setMemoedColumns] = React.useState<TableColumn[]>([])

  const handlePageSizeChange = React.useCallback(
    (value: number) => {
      onPageSizeChange?.(value)
      onPageChange?.(1)
    },
    [onPageChange, onPageSizeChange],
  )

  const handlePageChange = React.useCallback(
    (newPage: number) => {
      onPageChange?.(newPage)
    },
    [onPageChange],
  )

  React.useEffect(() => {
    setMemoedColumns(sortTable(columns))
  }, [columns])
  const stickyCount = React.useMemo(
    () => reduce((accum, col) => (col.sticky === 'left' ? accum + 1 : accum), 0, memoedColumns),
    [memoedColumns],
  )
  return (
    <>
      {data.length > 0 && memoedColumns.length > 0 ? (
        <div style={{ height, width, ...(style && { ...style }) }}>
          <TableGrid $columnCount={memoedColumns.length} $rowCount={data.length}>
            <TableHeader
              columns={memoedColumns}
              selectAll={selectAll}
              stickyCount={stickyCount}
              tableActions={tableActions}
              editAction={editAction}
            />
            {data.map((row, rowIndex) => (
              <TableRow
                key={row.id || row.subId}
                columns={memoedColumns}
                row={row}
                rowIndex={rowIndex}
                selection={selection}
                stickyCount={stickyCount}
                rowAction={rowAction}
              />
            ))}
          </TableGrid>
          <Footer>
            {onPageChange && (
              <TablePagination
                namespace="Contacts"
                page={page}
                size={pageSize}
                total={total}
                pageSizeValues={PAGE_SIZE_VALUES}
                onPageSizeChange={handlePageSizeChange}
                onPageChange={handlePageChange}
              />
            )}
          </Footer>
        </div>
      ) : (
        noData
      )}
    </>
  )
}

const calculateLeftOfColumn = (sticky: string | undefined, index: number) => {
  if (index === 0) {
    return {
      left: 0,
    }
  }
  if (sticky === 'left') {
    return {
      left: `${40 + (index - 1) * CELL_WIDTH}px`,
    }
  }
  if (sticky === 'right') {
    return {
      right: 0,
    }
  }
  return {
    left: 'auto',
  }
}

const sortTable = (tableColumns: TableColumn[]): TableColumn[] =>
  sort((a: TableColumn, b: TableColumn) => {
    if (!a.sticky && !b.sticky) {
      return 0
    }
    if (a.sticky === b.sticky) {
      return 0
    }
    if (a.sticky === 'left') {
      return -1
    }
    if (b.sticky === 'left') {
      return 1
    }
    if (a.sticky === 'right') {
      return 1
    }
    if (b.sticky === 'right') {
      return -1
    }
    return 0
  }, tableColumns)

type TableHeaderProps = {
  columns: TableColumn[]
  selectAll?: JSX.Element
  stickyCount: number
  tableActions: React.ReactNode

  editAction?: (payload: { id: TableColumn['accessor']; title: TableColumn['header'] }) => void
}

type TableRowProps = {
  columns: TableColumn[]
  row: ContactDataType
  rowIndex: number
  selection?: Selection
  stickyCount: number

  rowAction: (rowId: string | number) => void
}

type TableCellProps = {
  cellValue: any
  column: TableColumn
  hasSelection: boolean
  isChecked?: boolean
  isClickable: boolean
  isDataCell: boolean
  isEvenRow: boolean
  isLastSticky: boolean
  isRightSticky: boolean
  isSticky: boolean
  rowId: string | number
  style: React.CSSProperties

  rowAction: (rowId: string | number) => void
  selectionAdd?: Selection['add']
  selectionRemove?: Selection['remove']
}

const TableHeader = React.memo<TableHeaderProps>(({ columns, selectAll, stickyCount, tableActions, editAction }) => (
  <>
    {columns.map((column, index) => (
      <HeaderCell
        key={`${column.accessor}-${index}`}
        title={column.header}
        $isSticky={!!column.sticky}
        $isLastSticky={index === stickyCount - 1}
        $isRightSticky={column.sticky === 'right'}
        $isDataCell={index > 0}
        style={calculateLeftOfColumn(column.sticky, index)}
      >
        <HeaderText variant="body1" color="textSecondary">
          {column.header}
        </HeaderText>
        {index > 0 && index < columns.length && <ColumnActions column={column} editAction={editAction} />}
        {index === 0 && selectAll}
        {index === columns.length - 1 && tableActions}
      </HeaderCell>
    ))}
  </>
))

const TableRow = React.memo<TableRowProps>(({ columns, row, rowIndex, selection, stickyCount, rowAction }) => {
  const id = row.id || row.subId

  return (
    <RowWrapper className="power-table-row" key={id}>
      {columns.map((column, index) => (
        <TableCell
          key={`${id}-${column.accessor}`}
          cellValue={row[column.accessor]}
          column={column}
          hasSelection={!!selection}
          isChecked={selection?.isSelected(row[column.accessor])}
          isClickable={index > 0 && index < columns.length}
          isDataCell={index > 0}
          isEvenRow={rowIndex % 2 === 0}
          isLastSticky={index === stickyCount - 1}
          isRightSticky={column.sticky === 'right'}
          isSticky={!!column.sticky}
          rowId={id}
          style={calculateLeftOfColumn(column.sticky, index)}
          rowAction={rowAction}
          selectionAdd={selection?.add}
          selectionRemove={selection?.remove}
        />
      ))}
    </RowWrapper>
  )
})

const TableCell = React.memo<TableCellProps>(
  ({
    cellValue,
    column,
    hasSelection,
    isChecked,
    isClickable,
    isDataCell,
    isEvenRow,
    isLastSticky,
    isRightSticky,
    isSticky,
    rowId,
    style,
    rowAction,
    selectionAdd,
    selectionRemove,
  }) => {
    const handleClick = React.useCallback(() => {
      if (isClickable) {
        rowAction(rowId)
      }
    }, [isClickable, rowId, rowAction])
    const handleCheckboxChange = React.useCallback(() => {
      if (!isChecked) {
        selectionAdd?.([cellValue])
        return
      }
      selectionRemove?.([cellValue])
    }, [cellValue, isChecked, selectionAdd, selectionRemove])
    const renderedCell = column.cell ? column.cell(cellValue) : null
    const dataCellContent =
      column.sticky === 'right' ? (
        <div>{renderedCell}</div>
      ) : (
        <CellText component="div" variant="body1">
          {column.cell ? renderedCell : cellValue}
        </CellText>
      )

    return (
      <BodyCell
        className="power-table-cell"
        title={column.cell ? '' : cellValue}
        $isSticky={isSticky}
        $isEvenRow={isEvenRow}
        $isLastSticky={isLastSticky}
        $isRightSticky={isRightSticky}
        $isDataCell={isDataCell}
        style={style}
        onClick={handleClick}
      >
        {isDataCell || !hasSelection ? (
          dataCellContent
        ) : (
          <Checkbox name="select-item-table" checked={isChecked} size="small" onChange={handleCheckboxChange} />
        )}
      </BodyCell>
    )
  },
  (prevProps, nextProps) => equals(prevProps, nextProps),
)

const TableGrid = styled('div', {
  shouldForwardProp: (prop) => prop !== '$columnCount' && prop !== '$rowCount',
})<{ $columnCount: number; $rowCount: number }>(({ $columnCount, $rowCount }) => ({
  display: 'grid',
  gridTemplateColumns: `40px repeat(${$columnCount - 1}, minmax(${CELL_WIDTH}px, auto))`,
  gridTemplateRows: `repeat(${$rowCount + 1}, minmax(auto, 40px))`,
  overflow: 'scroll',
  height: '100%',
  width: '100%',
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    border: '2px solid white',
    backgroundColor: 'rgba(0, 0, 0, .5)',
  },
  '&::-webkit-scrollbar:horizontal': {
    height: '11px',
  },
}))

const BaseCell = styled('div')({
  borderBottom: '1px solid rgb(224, 224, 224)',
  background: '#fff',
  justifyContent: 'flex-start',
  alignItems: 'center',
  display: 'flex',
})

const HeaderCell = styled(BaseCell, {
  shouldForwardProp: (prop) =>
    !['$isSticky', '$isLastSticky', '$isRightSticky', '$isDataCell'].includes(prop as string),
})<{
  $isSticky: boolean
  $isLastSticky: boolean
  $isRightSticky: boolean
  $isDataCell: boolean
}>(({ $isSticky, $isLastSticky, $isRightSticky, $isDataCell }) => ({
  position: 'sticky',
  top: 0,
  zIndex: $isSticky ? 20 : 10,
  lineHeight: 1.4,
  boxShadow: '0px 1px 1px rgba(224, 224, 224, 1)',
  '&:hover': {
    '& .power-table-column-actions': {
      opacity: 1,
    },
  },
  ...($isLastSticky && {
    borderRight: '1px solid rgb(224, 224, 224)',
  }),
  ...($isRightSticky && {
    borderLeft: '1px solid rgb(224, 224, 224)',
    display: 'flex',
    justifyContent: 'center',
  }),
  ...($isDataCell && {
    padding: '30px 15px',
  }),
}))

const BodyCell = styled(BaseCell, {
  shouldForwardProp: (prop) =>
    !['$isSticky', '$isEvenRow', '$isLastSticky', '$isRightSticky', '$isDataCell'].includes(prop as string),
})<{
  $isSticky: boolean
  $isEvenRow: boolean
  $isLastSticky: boolean
  $isRightSticky: boolean
  $isDataCell: boolean
}>(({ $isSticky, $isEvenRow, $isLastSticky, $isRightSticky, $isDataCell }) => ({
  ...($isSticky && {
    position: 'sticky',
    zIndex: 10,
    left: 0,
  }),
  ...($isEvenRow && {
    backgroundColor: '#fafafa',
  }),
  ...($isLastSticky && {
    borderRight: '1px solid rgb(224, 224, 224)',
  }),
  ...($isRightSticky && {
    borderLeft: '1px solid rgb(224, 224, 224)',
    display: 'flex',
    justifyContent: 'center',
  }),
  ...($isDataCell && {
    padding: '30px 15px',
  }),
}))

const HeaderText = styled(Typography)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 'bold',
  textTransform: 'uppercase',
})

const CellText = styled(Typography)<TypographyProps>({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'rgba(0, 0, 0, 0.66)',
  maxWidth: '100%',
})

const Footer = styled('div')({
  position: 'sticky',
  bottom: 0,
  right: 0,
  zIndex: 30,
  background: '#fff',
  boxShadow: '0px -1px 1px rgba(224, 224, 224, 1)',
  display: 'flex',
  justifyContent: 'center',
  alignSelf: 'center',
  padding: '10px',
  paddingRight: '60px',
})

const RowWrapper = styled('div')({
  display: 'contents',
  cursor: 'pointer',
  '&:hover': {
    '& div': {
      backgroundColor: '#f0f0f0',
    },
  },
})
